import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Sparkles, Send, Copy, Link2 } from "lucide-react";
import { toast } from "sonner";

const fmtTime = (t) => {
  try { return new Date(t).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
};

export default function InboxThread() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const endRef = useRef(null);

  const loadMessages = useCallback(async () => {
    try { setMessages(await api.listLeadMessages(id)); } catch { /* ignore */ }
  }, [id]);

  useEffect(() => {
    (async () => {
      try { setLead(await api.getLead(id)); }
      catch { toast.error("Thread not found"); navigate("/app/factory/inbox"); }
    })();
    loadMessages();
    const t = setInterval(loadMessages, 5000);
    return () => clearInterval(t);
  }, [id, loadMessages, navigate]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if (!input.trim()) return;
    setSending(true);
    try {
      await api.sendLeadMessage(id, input.trim());
      setInput("");
      await loadMessages();
    } catch { toast.error("Could not send"); }
    finally { setSending(false); }
  };

  const aiDraft = async () => {
    setAiLoading(true);
    toast.info("Drafting a reply with AI…");
    try {
      const r = await api.triageLead(id);
      const draft = r?.triage?.reply_draft;
      if (draft) { setInput(draft); toast.success("AI draft ready — edit & send"); }
      else toast.success("Triage done (no draft returned)");
      setLead(await api.getLead(id));
    } catch { toast.error("AI draft failed"); }
    finally { setAiLoading(false); }
  };

  const brandLink = lead ? `${window.location.origin}/m/${lead.brand_token}` : "";
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(brandLink); toast.success("Brand chat link copied"); }
    catch { toast.error("Copy failed"); }
  };

  const onKey = (e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send(); };

  return (
    <div className="max-w-3xl mx-auto space-y-5" data-testid="inbox-thread">
      <button data-testid="thread-back" onClick={() => navigate("/app/factory/inbox")} className="mono text-white/50 hover:text-kinetic inline-flex items-center gap-2">
        <ArrowLeft className="w-4 h-4" /> BACK TO INBOX
      </button>

      <div className="border border-white/10 bg-[#0C0C0C] p-5 flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="mono text-white/40">{(lead?.category || "client").toUpperCase()} · NEGOTIATION</div>
          <h1 className="text-3xl font-extrabold mt-1" data-testid="thread-title">{lead?.name || "…"}</h1>
          <div className="mono text-white/40 mt-1">{lead?.company || lead?.email || "—"}</div>
        </div>
        <div className="flex flex-col items-end gap-2">
          {lead?.deposit_status === "paid" && <span className="mono px-2 py-1 bg-neon text-black">DEPOSIT PAID</span>}
          {lead?.deposit_status === "pending" && <span className="mono px-2 py-1 bg-white/10 text-white/70">DEPOSIT PENDING</span>}
          <button data-testid="copy-brand-link" onClick={copyLink} className="mono px-3 py-1.5 border border-white/20 text-white/70 hover:border-kinetic hover:text-kinetic transition rounded-sm inline-flex items-center gap-2">
            <Link2 className="w-3.5 h-3.5" /> COPY BRAND LINK <Copy className="w-3 h-3" />
          </button>
        </div>
      </div>

      <div className="border border-white/10 bg-[#0A0A0A] h-[46vh] overflow-y-auto p-5 space-y-3" data-testid="thread-messages">
        {messages.length === 0 && <div className="mono text-white/30 text-center py-16">NO MESSAGES YET · SAY HELLO 👋</div>}
        {messages.map(m => {
          const mine = m.sender === "creator";
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.sender}`}>
              <div className={`max-w-[78%] px-4 py-2.5 rounded-sm ${mine ? "bg-kinetic text-black" : "bg-white/10 text-white"}`}>
                <div className="mono text-[10px] opacity-60 mb-1">{mine ? "YOU" : (lead?.name || "BRAND").toUpperCase()} · {fmtTime(m.created_at)}</div>
                <div className="whitespace-pre-wrap text-sm leading-relaxed">{m.body}</div>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      <div className="border border-white/10 bg-[#0C0C0C] p-4 space-y-3">
        <Textarea data-testid="thread-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={onKey}
          placeholder="Type your reply… (⌘/Ctrl + Enter to send)" className="bg-black border-white/15 rounded-sm min-h-24" />
        <div className="flex items-center justify-between gap-2">
          <Button data-testid="thread-ai-draft" onClick={aiDraft} disabled={aiLoading} variant="outline"
            className="rounded-sm border-white/20 bg-transparent text-white/80 hover:border-kinetic hover:text-kinetic">
            <Sparkles className="w-4 h-4 mr-2" /> {aiLoading ? "DRAFTING…" : "AI DRAFT"}
          </Button>
          <Button data-testid="thread-send" onClick={send} disabled={sending || !input.trim()} className="kinetic-btn rounded-sm">
            <Send className="w-4 h-4 mr-2" /> {sending ? "SENDING…" : "Send"}
          </Button>
        </div>
      </div>
    </div>
  );
}
